<template>
  <Modal
    class-name="accounting class-confirmation-modal"
    title="Confirm Archive"
    :width="500"
    @hide-modal="emit('hide-modal')"
  >
    <div class="mb-2">
      <div>Are you sure you want to archive this accounting class?</div>
    </div>
    <div>
      <div class="my-2">
        <div v-if="hasNoUsages" data-cy="noUsages">No usages found.</div>
        <div v-else>
          <div>This accounting class is referenced by:</div>
          <div class="summary">
            <div
              v-for="usage_type in Object.keys(accountingClass?.usages)"
              :key="usage_type"
            >
              <div
                v-if="accountingClass?.usages[usage_type] > 0"
                :data-cy="usage_type + 'Count'"
              >
                • {{ accountingClass?.usages[usage_type] }}
                {{
                  formatUsageLabel(
                    usage_type,
                    accountingClass?.usages[usage_type]
                  )
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="modal-footer">
        <SeraButton
          class="mr-2"
          text="Cancel"
          type="secondary"
          @click="handleHideArchiveConfirm"
        />
        <SeraButton
          data-cy="archiveConfirmButton"
          type="danger"
          text="Archive"
          @click="handleArchive"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { inflect } from "inflection";
import { computed, ref } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import { AccountingClass } from "@/typings/accounting";

const emit = defineEmits(["hide-modal", "archived"]);

const props = defineProps({
  accountingClass: {
    type: Object as () => AccountingClass,
    required: true,
    default: null
  }
});

const hasNoUsages = computed(() => {
  const count = Object.values(props.accountingClass?.usages).reduce(
    (acc, count) => acc + count
  );
  return count === 0;
});

const store = useStore();
const isArchiving = ref(false);

function formatUsageLabel(key: string, value: number) {
  return inflect(key.replace("_", " "), value);
}

function handleHideArchiveConfirm() {
  emit("hide-modal");
}

async function handleArchive() {
  isArchiving.value = true;
  try {
    await store.dispatch("archiveAccountingClass", props.accountingClass);
    emit("hide-modal");
    emit("archived");
  } catch (e) {
    console.error(e);
  }
  isArchiving.value = false;
}
</script>

<style lang="scss">
.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}
</style>
